import {MorgiIcon} from '@/assets/icons/icons'
import {getLocalDate} from '@/dayjs'
import {useMe} from '@/features/authentication/queries/useMe'
import {AmountItem} from '@/features/gift/components/amount-item/AmountItem'
import {useRootStore} from '@/store'
import {selectCancelRecurringGiftModal, selectChoosePaymentsMethodModal, selectErrorModal} from '@/store/selectors'
import {REDIRECT_MAP} from '@/utilities/constants'
import {FC, useEffect, useState} from 'react'
import {Trans, useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router-dom'
import {useFetchSubscribedAmounts} from '../queries/useFetchSubscribedAmounts'
import {DonationType} from '../types'
import {
    StyledAnimationBox,
    StyledButtonContainer,
    StyledCancelButton,
    StyledCancelRecurringGiftModalContainer,
    StyledCancelRecurringGiftModalDescription,
    StyledCancelRecurringGiftModalStepSecondContainer,
    StyledCancelRecurringGiftModalStepSecondDescription,
    StyledCancelRecurringGiftModalStepSecondTitle,
    StyledCancelRecurringGiftModalStepThird,
    StyledCancelRecurringGiftModalStepThirdContainer,
    StyledCancelRecurringGiftModalStepThirdDescription,
    StyledCancelRecurringGiftModalStepThirdTitle,
    StyledConfirmButton,
    StyledHostsAvatar,
    StyledLimitedAmountsList,
    StyledMeetButton,
    StyledRejectButton
} from './style'
import Cry from '@/assets/animations/Cry.json'
import {Animation} from '@/components/ui/animation/Animation'
import {Modal} from '@/components/ui/modal/Modal'
import {SubscriptionStatusesE} from '@/features/chat/chat.model'
import {useGetCreatorProfile} from '@/features/guest/queries/useGetCreatorProfile'
import {useSubscribeToHostV3} from '../queries/useSubscribeToHostV3'
import {useUnSubscribeFromHostV3} from '../queries/useUnSubscribeFromHostV3'
import {useGetSubscribedHostsV3} from '../queries/useGetSubscribedHostsV3'

type steps = 1 | 2 | 3
type StepProps = {onChangePage: (step: steps) => void; closeModal: () => void}
const STEPS_MAP = {
    1: (props: StepProps) => <CancelRecurringGiftModalStepOne {...props} />,
    2: (props: StepProps) => <CancelRecurringGiftModalStepSecond {...props} />,
    3: (props: StepProps) => <CancelRecurringGiftModalStepThird {...props} />
}

export const CancelRecurringGiftModal: FC = () => {
    const {t} = useTranslation()
    const {closeModal} = useRootStore(selectCancelRecurringGiftModal)
    const [step, setStep] = useState<steps>(1)
    return (
        <Modal title={t('cancel_rookie_gift:modal_title')} onClose={closeModal} width="350px">
            <StyledCancelRecurringGiftModalContainer>
                {STEPS_MAP[step]({
                    onChangePage: setStep,
                    closeModal: () => {
                        closeModal()
                    }
                })}
            </StyledCancelRecurringGiftModalContainer>
        </Modal>
    )
}

const CancelRecurringGiftModalStepOne: FC<StepProps> = ({onChangePage, closeModal}) => {
    const {t} = useTranslation()
    const {hostID} = useRootStore(selectCancelRecurringGiftModal)
    const {mutate: subscribeToHost, error: subscribeToHostError} = useSubscribeToHostV3()
    const {data: amountsList} = useFetchSubscribedAmounts(false)
    const {data: subscribedHosts} = useGetSubscribedHostsV3({
        enabled: true,
        status: SubscriptionStatusesE.ACTIVE
    })
    const activeSubscribedHost = subscribedHosts?.find(({rookieId}) => rookieId.toString() === hostID)
    const limitedAmountsList = amountsList?.filter(({amount}) => amount < Number(activeSubscribedHost?.amount))
    const {data: host} = useGetCreatorProfile({creatorIdentifier: hostID ?? null})
    const usersFirstName = host?.fullName
    const {openModal: openChoosePaymentsMethodModal} = useRootStore(selectChoosePaymentsMethodModal)
    const {openErrorModal} = useRootStore(selectErrorModal)

    const onSubmit = () => {
        if (selectedAmount) {
            subscribeToHost({
                hostID: Number(hostID),
                subscriptionId: +selectedAmount?.id,
                userDiscountId: selectedAmount.discount?.id
            })
            closeModal()
        } else if (!limitedAmountsList?.length) {
            closeModal()
        }
    }

    useEffect(() => {
        if (subscribeToHostError) {
            if (subscribeToHostError.response?.status === 303) {
                openChoosePaymentsMethodModal({
                    paymentMethodUrls: subscribeToHostError.response?.data?.url ?? null,
                    description: 'choose_payment_method:description_micromorgi'
                })
            } else {
                openErrorModal({message: subscribeToHostError.message || null})
            }
        }
    }, [subscribeToHostError])

    const [selectedAmount, setSelectedAmount] = useState<DonationType | null>(null)
    const isDisabledButton = !selectedAmount && !!limitedAmountsList?.length
    return (
        <>
            <StyledCancelRecurringGiftModalDescription>
                {t('cancel_rookie_gift:first_step:description', {USER: usersFirstName})}
            </StyledCancelRecurringGiftModalDescription>
            <StyledLimitedAmountsList>
                {limitedAmountsList?.map(limitedAmount => (
                    <AmountItem
                        key={limitedAmount.id}
                        amount={limitedAmount}
                        isSelected={selectedAmount?.id === limitedAmount.id}
                        onSelect={() => setSelectedAmount(limitedAmount)}
                        amountIcon={<MorgiIcon height={31} width={31} />}
                    />
                ))}
            </StyledLimitedAmountsList>
            <StyledRejectButton onClick={onSubmit} disabled={isDisabledButton}>
                {t('cancel_rookie_gift:first_step:reject_button', {USER: usersFirstName})}
            </StyledRejectButton>
            <StyledConfirmButton
                onClick={() => {
                    onChangePage(2)
                }}
                variant="outlined"
            >
                {t('cancel_rookie_gift:first_step:confirm_button')}
            </StyledConfirmButton>
        </>
    )
}

const CancelRecurringGiftModalStepSecond: FC<StepProps> = ({onChangePage}) => {
    const {t} = useTranslation()
    const {hostID, closeModal, subscriptionId} = useRootStore(selectCancelRecurringGiftModal)
    const {data: host} = useGetCreatorProfile({creatorIdentifier: hostID ?? null})
    const {mutate: deleteRecurringGift, data: dataOfDeleteRecurringGift} = useUnSubscribeFromHostV3(
        subscriptionId ?? ''
    )
    const usersFirstName = host?.fullName
    const onCancelSupport = () => {
        deleteRecurringGift()
    }
    useEffect(() => {
        if (dataOfDeleteRecurringGift) onChangePage(3)
    }, [dataOfDeleteRecurringGift])

    return (
        <>
            <StyledCancelRecurringGiftModalStepSecondContainer>
                <StyledHostsAvatar $src={String(host?.avatar?.url)} />
                <StyledCancelRecurringGiftModalStepSecondTitle>
                    {t('cancel_rookie_gift:second_step:title')}
                </StyledCancelRecurringGiftModalStepSecondTitle>

                <StyledCancelRecurringGiftModalStepSecondDescription>
                    <Trans
                        t={t}
                        // i18nKey={`cancel_rookie_gift:second_step:description_${host?.gender?.key_name}`}
                        i18nKey={`cancel_rookie_gift:second_step:description`}
                        components={{u: <u />}}
                        values={{
                            USER: String(usersFirstName)
                        }}
                    />
                </StyledCancelRecurringGiftModalStepSecondDescription>
            </StyledCancelRecurringGiftModalStepSecondContainer>
            <StyledButtonContainer>
                <StyledRejectButton onClick={closeModal}>
                    {t('cancel_rookie_gift:second_step:reject_button')}
                </StyledRejectButton>
                <StyledCancelButton onClick={onCancelSupport} variant="transparent">
                    {t('cancel_rookie_gift:second_step:confirm_button')}
                </StyledCancelButton>
            </StyledButtonContainer>
        </>
    )
}

const CancelRecurringGiftModalStepThird: FC<StepProps> = ({closeModal}) => {
    const {t} = useTranslation()
    const navigate = useNavigate()
    const {data: user} = useMe()
    const {hostID} = useRootStore(selectCancelRecurringGiftModal)
    const {data: subscribedHosts} = useGetSubscribedHostsV3({
        enabled: true,
        status: SubscriptionStatusesE.ACTIVE
    })
    const {data: host} = useGetCreatorProfile({
        creatorIdentifier: hostID ?? null
    })
    const activeSubscribedHost = subscribedHosts?.find(({rookieId}) => rookieId.toString() === hostID)
    const usersFirstName = host?.fullName

    if (!user) return <></>

    return (
        <StyledCancelRecurringGiftModalStepThirdContainer>
            <StyledAnimationBox>
                <Animation animationData={Cry} />
            </StyledAnimationBox>
            <StyledCancelRecurringGiftModalStepThird>
                <StyledCancelRecurringGiftModalStepThirdTitle>
                    {t('cancel_rookie_gift:status_step:title')}
                </StyledCancelRecurringGiftModalStepThirdTitle>
                <StyledCancelRecurringGiftModalStepThirdDescription>
                    {t('cancel_rookie_gift:status_step:description', {
                        USER: usersFirstName,
                        DATE: activeSubscribedHost?.validUntilAt
                            ? getLocalDate(String(activeSubscribedHost.validUntilAt), 'DD MMMM')
                            : ''
                    })}
                </StyledCancelRecurringGiftModalStepThirdDescription>
            </StyledCancelRecurringGiftModalStepThird>

            <StyledMeetButton
                onClick={() => {
                    closeModal()
                    navigate(REDIRECT_MAP[user.type])
                }}
            >
                {t('cancel_rookie_gift:status_step:button')}
            </StyledMeetButton>
        </StyledCancelRecurringGiftModalStepThirdContainer>
    )
}
